import React, { useEffect, useMemo, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { billingQueue as urls } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { InlineBillingFields, getDateErrors } from './InlineBillingFields'
import { CreateInvoiceAccountModal } from './CreateInvoiceAccountModal'

const initEditValues = br => ({
  school_id: br.school ? br.school.id : '',
  invoice_account_id: br.invoice_account ? br.invoice_account.id : '',
  invoice_type: br.invoice_type || '',
  billing_price: br.billing_price || '',
  billing_date: br.billing_date || '',
  enrollment_start: br.enrollment_start || '',
  enrollment_end: br.enrollment_end || '',
  billing_frequency: br.billing_frequency || '',
  billing_frequency_custom: br.billing_frequency_custom || '',
})

const canSaveBilling = vals => {
  if (!vals.invoice_account_id || !vals.invoice_type || !vals.billing_price) return false
  if (vals.invoice_type === 'one_time') {
    if (!vals.billing_date) return false
    const { enrollmentError, billingDateError } = getDateErrors(vals)
    if (enrollmentError || billingDateError) return false
    return true
  }
  if (vals.invoice_type === 'recurring') {
    if (!vals.billing_frequency) return false
    if (vals.billing_frequency === 'custom') return !!vals.billing_frequency_custom?.trim()
    return true
  }
  return false
}

export const AssignedItem = ({ br }) => {
  const [state, dispatch] = useStateValue()
  const { user, schools } = state
  const [isEditing, setIsEditing] = useState(false)
  const [editValues, setEditValues] = useState(initEditValues(br))
  // Seed with the current account so the select isn't blank while the full list loads.
  const [schoolAccounts, setSchoolAccounts] = useState(
    br.invoice_account ? [{ id: br.invoice_account.id, name: br.invoice_account.name }] : []
  )
  const [invoiceNumber, setInvoiceNumber] = useState(br.invoice_number || '')
  const [saving, setSaving] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const handleAccountCreated = newAccount => {
    setSchoolAccounts(prev => [...prev, newAccount])
    setEditValues(prev => ({ ...prev, invoice_account_id: newAccount.id }))
  }

  const loadSchoolAccounts = async schoolId => {
    if (!schoolId) {
      setSchoolAccounts([])
      return
    }
    try {
      const resp = await client.get(urls.getSchoolDetail(schoolId))
      setSchoolAccounts(resp.data.invoice_accounts || [])
    } catch {
      setSchoolAccounts([])
    }
  }

  const handleEditChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }))
  }

  const handleSchoolChange = async schoolId => {
    setEditValues(prev => ({ ...prev, school_id: schoolId, invoice_account_id: '' }))
    await loadSchoolAccounts(schoolId)
  }

  const handleCancelEdit = () => {
    setEditValues(initEditValues(br))
    setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing && editValues.school_id) {
      loadSchoolAccounts(editValues.school_id)
    }
  }, [isEditing])

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      const resp = await client.post(urls.updateBilling(), {
        billing_record_id: br.id,
        invoice_account_id: editValues.invoice_account_id,
        invoice_type: editValues.invoice_type,
        billing_price: editValues.billing_price,
        billing_date: editValues.billing_date || null,
        enrollment_start: editValues.enrollment_start || null,
        enrollment_end: editValues.enrollment_end || null,
        billing_frequency: editValues.billing_frequency || null,
        billing_frequency_custom: editValues.billing_frequency_custom || null,
      })
      dispatch({ type: 'UPDATE_ASSIGNED_ITEM', payload: resp.data })
      setIsEditing(false)
      toast.success('Billing details updated.')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update billing.')
    }
    setSaving(false)
  }

  const handleInvoiceNumberBlur = async () => {
    if (invoiceNumber === (br.invoice_number || '')) return
    try {
      const resp = await client.post(urls.setInvoiceNumber(), {
        billing_record_id: br.id,
        invoice_number: invoiceNumber,
      })
      dispatch({
        type: 'UPDATE_ASSIGNED_ITEM',
        payload: { ...br, invoice_number: resp.data.invoice_number },
      })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save invoice number.')
    }
  }

  const handleInvoiceNumberKeyDown = e => {
    if (e.key === 'Enter') e.target.blur()
  }

  const handlePaidChange = async e => {
    const newStatus = e.target.checked ? 'paid' : 'unpaid'
    try {
      const resp = await client.post(urls.setPaymentStatus(), {
        billing_record_id: br.id,
        payment_status: newStatus,
      })
      dispatch({
        type: 'UPDATE_ASSIGNED_ITEM',
        payload: { ...br, payment_status: resp.data.payment_status },
      })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update payment status.')
    }
  }

  // Guarantee the current school is always in the options list even if global
  // schools state hasn't loaded yet — Combobox needs it to derive selectedLabel.
  const effectiveSchools = useMemo(() => {
    if (!br.school) return schools
    if (schools.some(s => String(s.id) === String(br.school.id))) return schools
    return [{ id: br.school.id, name: br.school.name }, ...schools]
  }, [schools, br.school])

  const isPaid = br.payment_status === 'paid'
  const isOneTime = br.invoice_type === 'one_time'
  const typeLabel = isOneTime ? 'One-time' : 'Recurring'
  const dateErrors = getDateErrors(editValues)
  const canSave = canSaveBilling(editValues)

  return (
    <div className={`card-myed mb-2 ${isEditing ? 'border-primary' : ''}`}>
      <div className="card-body py-3">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-1">
          <strong>{br.course.title}</strong>
          <button
            className="btn btn-sm btn-myed btn-default btn-default-myed ml-2"
            onClick={() => setIsEditing(!isEditing)}
          >
            <i className="fa-regular fa-edit mr-1" />
            Edit
          </button>
        </div>

        {/* Meta chips */}
        <div className="d-flex flex-wrap mb-2" style={{ gap: '6px' }}>
          {br.school && (
            <span className="label label-primary">{br.school.name}</span>
          )}
          <span className="label label-default">Course Number: {br.course.coursenum}</span>
          {br.invoice_account && (
            <span className="label label-default">{br.invoice_account.name}</span>
          )}
          <span className={`label ${isOneTime ? 'label-primary' : 'label-success'}`}>
            {typeLabel}
          </span>
          {br.billing_price && (
            <span className="label label-default">${br.billing_price}</span>
          )}
        </div>

        {br.sales_reps && br.sales_reps.length > 0 && (
          <p className="text-muted small mb-2">
            <i className="fa-regular fa-user mr-1" />
            {br.sales_reps.join(', ')}
          </p>
        )}

        {/* Invoice tracking (one-time only, finance users) */}
        {isOneTime && user.isFinance && !isEditing && (
          <div className="d-flex align-items-end flex-wrap mt-2" style={{ gap: '16px' }}>
            <div>
              <label className="small text-muted mb-1 d-block">Invoice #</label>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: '130px', fontFamily: 'monospace' }}
                placeholder="—"
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                onBlur={handleInvoiceNumberBlur}
                onKeyDown={handleInvoiceNumberKeyDown}
              />
            </div>
            <div className="pb-1">
              <label className="d-flex align-items-center" style={{ cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={isPaid}
                  onChange={handlePaidChange}
                />
                <span className={`small ${isPaid ? 'text-success font-weight-bold' : 'text-muted'}`}>
                  {isPaid ? 'Paid' : 'Mark paid'}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Recurring note */}
        {!isOneTime && !isEditing && (
          <p className="small text-muted mb-0 mt-1">
            <i className="fa-regular fa-sync-alt mr-1" />
            Recurring — invoices managed in billing system
          </p>
        )}

        {/* Inline edit form */}
        {isEditing && (
          <div className="mt-3 pt-3 border-top">
            <InlineBillingFields
              schools={effectiveSchools}
              schoolAccounts={schoolAccounts}
              values={editValues}
              onChange={handleEditChange}
              onSchoolChange={handleSchoolChange}
              onOpenCreateModal={() => setCreateModalOpen(true)}
              errors={dateErrors}
            />
            <CreateInvoiceAccountModal
              show={createModalOpen}
              toggle={setCreateModalOpen}
              defaultSchoolId={editValues.school_id || null}
              onCreated={handleAccountCreated}
            />
            <div className="d-flex justify-content-end mt-2" style={{ gap: '8px' }}>
              <button
                className="btn btn-sm btn-myed btn-default btn-default-myed"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-myed btn-primary btn-primary-myed"
                onClick={handleSaveEdit}
                disabled={saving || !canSave}
              >
                {saving ? (
                  <i className="fa-regular fa-spinner fa-spin mr-1" />
                ) : (
                  <i className="fa-regular fa-check mr-1" />
                )}
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
